import React from 'react';
import { addDays, addWeeks, startOfWeek, endOfWeek, addMonths, addYears, isSameMonth } from 'date-fns';
import { useDayPicker } from '../DayPicker/useDayPicker.js';
import '../DayPicker/DayPickerContext.js';
import '../Navigation/NavigationContext.js';
import { useNavigation } from '../Navigation/useNavigation.js';

/**
 * The Focus context shares details about the focused day for the keyboard navigation.
 *
 * Access this context from the [[useFocus]] hook.
 */
var FocusContext = React.createContext(undefined);
/** The provider for the [[FocusContext]]. */
function FocusProvider(_a) {
    var children = _a.children;
    var _b = React.useState(), focusedDay = _b[0], setDay = _b[1];
    var _c = useNavigation(), goToMonth = _c.goToMonth, displayMonths = _c.displayMonths;
    var numberOfMonths = useDayPicker().numberOfMonths;
    var blur = function () { return setDay(undefined); };
    var focus = function (date) { return setDay(date); };
    var switchMonth = function (date, offset) {
        if (displayMonths.some(function (m) { return isSameMonth(date, m); }))
            return;
        if (offset < 0) {
            goToMonth(addMonths(date, 1 + offset));
        }
        else {
            goToMonth(date);
        }
    };
    var focusDayBefore = function () {
        if (!focusedDay)
            return;
        var before = addDays(focusedDay, -1);
        focus(before);
        switchMonth(before, numberOfMonths * -1);
    };
    var focusDayAfter = function () {
        if (!focusedDay)
            return;
        var after = addDays(focusedDay, 1);
        focus(after);
        switchMonth(after, numberOfMonths);
    };
    var focusWeekBeforeDay = function () {
        if (!focusedDay)
            return;
        var up = addWeeks(focusedDay, -1);
        focus(up);
        switchMonth(up, numberOfMonths * -1);
    };
    var focusWeekAfterDay = function () {
        if (!focusedDay)
            return;
        var down = addWeeks(focusedDay, 1);
        focus(down);
        switchMonth(down, numberOfMonths);
    };
    var focusStartOfWeek = function () {
        if (!focusedDay)
            return;
        var dayToFocus = startOfWeek(focusedDay);
        switchMonth(dayToFocus, numberOfMonths);
        focus(dayToFocus);
    };
    var focusEndOfWeek = function () {
        if (!focusedDay)
            return;
        var dayToFocus = endOfWeek(focusedDay);
        switchMonth(dayToFocus, numberOfMonths);
        focus(dayToFocus);
    };
    var focusMonthBefore = function () {
        if (!focusedDay)
            return;
        var monthBefore = addMonths(focusedDay, -1);
        switchMonth(monthBefore, numberOfMonths);
        focus(monthBefore);
    };
    var focusMonthAfter = function () {
        if (!focusedDay)
            return;
        var monthAfter = addMonths(focusedDay, 1);
        switchMonth(monthAfter, numberOfMonths);
        focus(monthAfter);
    };
    var focusYearBefore = function () {
        if (!focusedDay)
            return;
        var yearBefore = addYears(focusedDay, -1);
        switchMonth(yearBefore, numberOfMonths);
        focus(yearBefore);
    };
    var focusYearAfter = function () {
        if (!focusedDay)
            return;
        var yearAfter = addYears(focusedDay, 1);
        switchMonth(yearAfter, numberOfMonths);
        focus(yearAfter);
    };
    var setters = {
        blur: blur,
        focus: focus,
        focusDayAfter: focusDayAfter,
        focusDayBefore: focusDayBefore,
        focusWeekAfterDay: focusWeekAfterDay,
        focusWeekBeforeDay: focusWeekBeforeDay,
        focusMonthBefore: focusMonthBefore,
        focusMonthAfter: focusMonthAfter,
        focusYearBefore: focusYearBefore,
        focusYearAfter: focusYearAfter,
        focusStartOfWeek: focusStartOfWeek,
        focusEndOfWeek: focusEndOfWeek
    };
    return (React.createElement(FocusContext.Provider, { value: [focusedDay, setters] }, children));
}

export { FocusContext, FocusProvider };
//# sourceMappingURL=FocusContext.js.map
